import SceneObject from "../../../lib/services/scene/sceneobject";
import Navigator, { NavigationStyle } from "../../../lib/services/collision/navigator";
import Puppy from "./puppy";
import { PuppyClips, PuppyClipId } from "./puppyclips";
import Stage from "../../../lib/services/stage/stage";
import CollisionService from "../../../lib/services/collision/collision_service";
import SphereCollisionVolume from "../../../lib/services/collision/sphere_collision_volume";

import {
  PuppyMoveLoop,
  PuppyPosture,
  PuppyRestLoop,
  PuppyTransitionPlan,
  PuppyTravelMode,
} from "./puppytypes";
import { resolve_transition_plan } from "./puppytransitions";

type ArrivalChain = {
  oneShots: PuppyClipId[];
  finalLoop: PuppyClipId;
};

type PuppyGoal =
  | { type: "none" }
  | {
      type: "point";
      destination: Float32Array;
      style: NavigationStyle;
      arriveRadius: number;
      arrivalChain: ArrivalChain | null;
    }
  | {
      type: "object";
      target: SceneObject;
      style: NavigationStyle;
      arriveRadius: number;
      arrivalChain: ArrivalChain | null;
    };

type NavigatorPhase =
  | "idle"
  | "enter_transition"
  | "moving"
  | "arrive_transition";

const DEFAULT_BLEND_IN_SECONDS = 0.2;
const SHORT_BLEND_IN_SECONDS = 0.12;

export default class PuppyNavigator extends Navigator {
  private puppy: Puppy;
  private goal: PuppyGoal = { type: "none" };

  private posture: PuppyPosture = "idle";
  private phase: NavigatorPhase = "idle";
  private activePlan: PuppyTransitionPlan | null = null;

  private currentLoop: PuppyMoveLoop | PuppyRestLoop | null = null;
  private expectedLoopAfterTransition: PuppyMoveLoop | PuppyRestLoop | null = null;
  private clearGoalWhenLoopReached = false;

  private collision_service: CollisionService;

  private probe_distance = 0.4;//0.6;
  private probe_radius = 0.15;  //was 0.35
  private steering_angles = [25, 45, 70, 90, 120];
  //private max_turn_rate_rad_per_sec = Math.PI * 2.0; // too jump 
  private max_turn_rate_rad_per_sec = Math.PI * 1.2; // 216 degrees/sec — snappy but smooth
 // private max_turn_rate_rad_per_sec = Math.PI * 0.8; // 144 degrees/sec — noticeably smooth
  //private max_turn_rate_rad_per_sec = Math.PI * 0.5; // 90 degrees/sec — gentle, dog-like


  private last_deflection_sign = 0;
  private deflection_commit_time = 0;
  private commit_duration =0.25; // 0.45;

  private stuck_check_time = 0;
  private stuck_check_interval = 0.5;
  private stuck_last_position = new Float32Array([0, 0, 0]);
  private stuck_progress_threshold = 0.15;

  constructor(puppy: Puppy) {
    super();
    this.collision_service = Stage.instance().collision_service;
    this.puppy = puppy;
    this.attach(puppy);
  }

  set_posture(posture: PuppyPosture): void {
    this.posture = posture;
  }

  get_posture(): PuppyPosture {
    return this.posture;
  }

  move_to_point(
    destination: Float32Array,
    style: NavigationStyle = "energetic",
    arriveRadius = 0.25,
    arrivalChain?: ArrivalChain
  ): void {
    this.goal = {
      type: "point",
      destination: new Float32Array(destination),
      style,
      arriveRadius,
      arrivalChain: arrivalChain ?? null,
    };

    this.begin_move_plan();
  }

  move_to_object(
    target: SceneObject,
    style: NavigationStyle = "energetic",
    arriveRadius = 0.25,
    arrivalChain?: ArrivalChain
  ): void {
    this.goal = {
      type: "object",
      target,
      style,
      arriveRadius,
      arrivalChain: arrivalChain ?? null,
    };

    this.begin_move_plan();
  }

  clear_goal(): void {
    this.goal = { type: "none" };
    this.currentRequest = null;
    this.activePlan = null;
    this.currentLoop = null;
    this.expectedLoopAfterTransition = null;
    this.clearGoalWhenLoopReached = false;
  }

  stop(): void {
    this.clear_goal();
    this.phase = "idle";
    this.play_rest_loop_for_posture();
  }

  override has_destination(): boolean {
    return this.goal.type !== "none";
  }

  private get_gpu_controller() {
    return this.puppy.animation_controller_gpu;
  }

  private navigation_style_to_travel_mode(style: NavigationStyle): PuppyTravelMode {
    switch (style) {
      case "casual":
        return "casual";
      case "energetic":
        return "energetic";
      case "aggressive":
        return "aggressive";
    }
  }

  private get_goal_position(): Float32Array | null {
    if (this.goal.type === "point") return this.goal.destination;
    if (this.goal.type === "object") {
      const p = this.goal.target.transform.position;
      return new Float32Array([p[0], p[1], p[2]]);
    }
    return null;
  }

  private get_goal_style(): NavigationStyle {
    if (this.goal.type === "point" || this.goal.type === "object") return this.goal.style;
    return "casual";
  }

  private get_goal_arrive_radius(): number {
    if (this.goal.type === "point" || this.goal.type === "object") return this.goal.arriveRadius;
    return 0.25;
  }

  private get_goal_arrival_chain(): ArrivalChain | null {
    if (this.goal.type === "point" || this.goal.type === "object") return this.goal.arrivalChain;
    return null;
  }

  private begin_move_plan(): void {
    const controller = this.get_gpu_controller();
    if (!controller) return;
    if (this.goal.type === "none") return;

    this.last_deflection_sign = 0;
    this.deflection_commit_time = 0;
    this.stuck_check_time = 0;
    this.stuck_last_position[0] = this.puppy.transform.position[0];
    this.stuck_last_position[1] = this.puppy.transform.position[1];
    this.stuck_last_position[2] = this.puppy.transform.position[2];

    const travelMode = this.navigation_style_to_travel_mode(this.get_goal_style());
    this.activePlan = resolve_transition_plan(this.posture, travelMode);

    if (this.activePlan.enterOneShots.length > 0) {
      this.phase = "enter_transition";
      this.currentLoop = null;
      this.expectedLoopAfterTransition = this.activePlan.moveLoop;
      this.clearGoalWhenLoopReached = false;

      const steps = [
        ...this.activePlan.enterOneShots.map((id) => ({
          clipIndex: id,
          blendInSeconds: SHORT_BLEND_IN_SECONDS,
          loop: false,
        })),
        {
          clipIndex: this.activePlan.moveLoop,
          blendInSeconds: DEFAULT_BLEND_IN_SECONDS,
          loop: true,
        },
      ];

      controller.playSequence(steps);
    } else {
      this.phase = "moving";
      this.play_loop_clip(this.activePlan.moveLoop);
    }
  }
  update_target_position(destination: Float32Array): void {
  if (this.goal.type !== "point") return;

  this.goal.destination[0] = destination[0];
  this.goal.destination[1] = destination[1];
  this.goal.destination[2] = destination[2];

  // Re-aiming invalidates any committed deflection so the puppy
  // re-evaluates probing toward the new target immediately.
  this.deflection_commit_time = 0;
}

request_arrive_here(): void {
  if (this.goal.type !== "point") return;

  const p = this.puppy.transform.position;
  this.goal.destination[0] = p[0];
  this.goal.destination[1] = p[1];
  this.goal.destination[2] = p[2];
  // Next tick: distance <= arriveRadius → navigator runs begin_arrival_plan().
}

  private begin_arrival_plan(): void {
    const controller = this.get_gpu_controller();
    if (!controller || !this.activePlan) return;

    const arrivalChain = this.get_goal_arrival_chain();

    if (arrivalChain) {
      this.phase = "arrive_transition";
      this.currentLoop = null;
      this.expectedLoopAfterTransition = arrivalChain.finalLoop as PuppyMoveLoop | PuppyRestLoop;
      this.clearGoalWhenLoopReached = true;

      const steps = [
        ...arrivalChain.oneShots.map((id) => ({
          clipIndex: id,
          blendInSeconds: SHORT_BLEND_IN_SECONDS,
          loop: false,
        })),
        {
          clipIndex: arrivalChain.finalLoop,
          blendInSeconds: DEFAULT_BLEND_IN_SECONDS,
          loop: true,
        },
      ];

      controller.playSequence(steps);

      if (this.is_rest_loop(arrivalChain.finalLoop)) {
        this.update_posture_from_rest_loop(arrivalChain.finalLoop);
      }
      return;
    }

    if (this.activePlan.arriveOneShots.length > 0) {
      this.phase = "arrive_transition";
      this.currentLoop = null;
      this.expectedLoopAfterTransition = this.activePlan.arriveLoop;
      this.clearGoalWhenLoopReached = true;

      const steps = [
        ...this.activePlan.arriveOneShots.map((id) => ({
          clipIndex: id,
          blendInSeconds: SHORT_BLEND_IN_SECONDS,
          loop: false,
        })),
        {
          clipIndex: this.activePlan.arriveLoop,
          blendInSeconds: DEFAULT_BLEND_IN_SECONDS,
          loop: true,
        },
      ];

      controller.playSequence(steps);

      if (this.is_rest_loop(this.activePlan.arriveLoop)) {
        this.update_posture_from_rest_loop(this.activePlan.arriveLoop);
      }
    } else {
      this.phase = "idle";
      this.play_loop_clip(this.activePlan.arriveLoop);

      if (this.is_rest_loop(this.activePlan.arriveLoop)) {
        this.update_posture_from_rest_loop(this.activePlan.arriveLoop);
      }

      this.clear_goal();
    }
  }

  private update_transition_state(): void {
    const controller = this.get_gpu_controller();
    if (!controller) return;

    const state = controller.get_state();
    const currentClipIndex = state.currentState?.clipIndex ?? null;
    const transitionActive = state.nextState !== null;

    if (
      this.phase === "enter_transition" &&
      !transitionActive &&
      this.expectedLoopAfterTransition !== null &&
      currentClipIndex === this.expectedLoopAfterTransition
    ) {
      this.currentLoop = this.expectedLoopAfterTransition;
      this.expectedLoopAfterTransition = null;
      this.phase = "moving";
      return;
    }

    if (
      this.phase === "arrive_transition" &&
      !transitionActive &&
      this.expectedLoopAfterTransition !== null &&
      currentClipIndex === this.expectedLoopAfterTransition
    ) {
      this.currentLoop = this.expectedLoopAfterTransition;
      this.expectedLoopAfterTransition = null;
      this.phase = "idle";

      if (this.clearGoalWhenLoopReached) {
        this.clearGoalWhenLoopReached = false;
        this.clear_goal();
      }
    }
  }
    private play_loop_clip(id: PuppyMoveLoop | PuppyRestLoop): void {
    const controller = this.get_gpu_controller();
    if (!controller) return;
    if (this.currentLoop === id) return;

    this.currentLoop = id;
    controller.playSequence([
      { clipIndex: id, blendInSeconds: DEFAULT_BLEND_IN_SECONDS, loop: true },
    ]);
  }

  private play_rest_loop_for_posture(): void {
    switch (this.posture) {
      case "sleeping":
        this.play_loop_clip(PuppyClips.Lie_Sleep_loop);
        break;
      case "lying":
        this.play_loop_clip(PuppyClips.Lie_loop_1);
        break;
      case "sitting":
        this.play_loop_clip(PuppyClips.Sitting_loop_1);
        break;
      case "idle":
      default:
        this.play_loop_clip(PuppyClips.Idle_1);
        break;
    }
  }

  private update_posture_from_rest_loop(loopClip: PuppyRestLoop): void {
    switch (loopClip) {
      case PuppyClips.Lie_Sleep_loop:
        this.posture = "sleeping";
        break;
      case PuppyClips.Lie_loop_1:
        this.posture = "lying";
        break;
      case PuppyClips.Sitting_loop_1:
        this.posture = "sitting";
        break;
      case PuppyClips.Idle_1:
      default:
        this.posture = "idle";
        break;
    }
  }

  private is_rest_loop(id: PuppyClipId): id is PuppyRestLoop {
    return (
      id === PuppyClips.Idle_1 ||
      id === PuppyClips.Sitting_loop_1 ||
      id === PuppyClips.Lie_loop_1 ||
      id === PuppyClips.Lie_Sleep_loop
    );
  }

  private distance_to_goal(goal: Float32Array): number {
    const p = this.puppy.transform.position;
    const dx = goal[0] - p[0];
    const dy = goal[1] - p[1];
    const dz = goal[2] - p[2];
    return Math.hypot(dx, dy, dz);
  }

  private wrap_angle_pi(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }

  private advance_with_root_motion(): void {
  const controller = this.get_gpu_controller();
  if (!controller) return;

  const rm = controller.rootMotionDelta.translation;
  const yaw = this.puppy.transform.rotation[1];

  const localX = rm[0];
  const localY = rm[1];
  const localZ = rm[2];

  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);

  const worldX = localX * cosY - localZ * sinY;
  const worldZ = localX * sinY + localZ * cosY;

  this.puppy.transform.position[0] += worldX;
  this.puppy.transform.position[1] += localY;
  this.puppy.transform.position[2] += worldZ;
  this.puppy.transform.rotation[1] = this.wrap_angle_pi(
    this.puppy.transform.rotation[1] + controller.rootMotionDelta.rotationY
  );
}

  private is_blocked(x: number, z: number, radius: number): boolean {
    return this.collision_service.isBlockedXZ(x, z, radius, "nav");
  }

  private compute_steered_yaw(goal_x: number, goal_z: number): number {
    const px = this.puppy.transform.position[0];
    const pz = this.puppy.transform.position[2];

    let dx = goal_x - px;
    let dz = goal_z - pz;
    const dist = Math.sqrt(dx * dx + dz * dz);

    if (dist < 0.001) return this.puppy.transform.rotation[1];

    dx /= dist;
    dz /= dist;

    const yaw_for = (vx: number, vz: number): number => {
      return this.wrap_angle_pi(Math.atan2(-vx, vz));
    };

    const probe_ok = (vx: number, vz: number): boolean => {
      const tx = px + vx * this.probe_distance;
      const tz = pz + vz * this.probe_distance;
      return !this.is_blocked(tx, tz, this.probe_radius);
    };

    const can_go_straight = this.deflection_commit_time <= 0;

    if (can_go_straight && probe_ok(dx, dz)) {
      this.last_deflection_sign = 0;
      return yaw_for(dx, dz);
    }

    const first_sign = this.last_deflection_sign !== 0 ? this.last_deflection_sign : -1;
    const second_sign = -first_sign;

    for (const deg of this.steering_angles) {
      const rad = deg * (Math.PI / 180);

      for (const sign of [first_sign, second_sign]) {
        const angle = sign * rad;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const sx = dx * cos - dz * sin;
        const sz = dx * sin + dz * cos;

        if (probe_ok(sx, sz)) {
          this.last_deflection_sign = sign;
          this.deflection_commit_time = this.commit_duration;
          return yaw_for(sx, sz);
        }
      }
    }

    return this.puppy.transform.rotation[1];
  }

  private turn_toward_yaw(target_yaw: number, delta_time: number): void {
    const current = this.puppy.transform.rotation[1];
    let diff = this.wrap_angle_pi(target_yaw - current);

    const max_step = this.max_turn_rate_rad_per_sec * delta_time;
    if (diff > max_step) diff = max_step;
    else if (diff < -max_step) diff = -max_step;

    this.puppy.transform.rotation[1] = this.wrap_angle_pi(current + diff);
  }

  private get_arrive_position(): Float32Array {
  for (const volume of this.puppy.collisionVolumes) {
    if (volume instanceof SphereCollisionVolume && volume.name === "head") {
      const sphere = volume.getWorldSphere();
      if (sphere) return sphere.center;
    }
  }
  return this.puppy.transform.position;
}

  tick(delta_time: number): void {
    // The GPU controller's tick and compute are driven by the puppy's
    // lifecycle hooks (Puppy.tick and Puppy.compute), not by the
    // navigator. The navigator only reads state and issues playSequence
    // calls.

    this.update_transition_state();

    if (this.deflection_commit_time > 0) {
      this.deflection_commit_time -= delta_time;
      if (this.deflection_commit_time < 0) this.deflection_commit_time = 0;
    }

    if (this.phase === "moving" && this.goal.type !== "none") {
      const goal = this.get_goal_position();

      if (goal) 
        {
          const arrive_pos = this.get_arrive_position();
          const dx = goal[0] - arrive_pos[0];
          const dz = goal[2] - arrive_pos[2];
          const distance = Math.sqrt(dx * dx + dz * dz);

        if (distance <= this.get_goal_arrive_radius()) {
          this.begin_arrival_plan();
        } else {
          this.stuck_check_time += delta_time;
          if (this.stuck_check_time >= this.stuck_check_interval) {
            const cur_x = this.puppy.transform.position[0];
            const cur_z = this.puppy.transform.position[2];

            const sdx = cur_x - this.stuck_last_position[0];
            const sdz = cur_z - this.stuck_last_position[2];
            const moved = Math.sqrt(sdx * sdx + sdz * sdz);

            if (moved < this.stuck_progress_threshold) {
              this.last_deflection_sign =
                this.last_deflection_sign === 0 ? -1 : -this.last_deflection_sign;
              this.deflection_commit_time = this.commit_duration;
            }

            this.stuck_last_position[0] = cur_x;
            this.stuck_last_position[1] = this.puppy.transform.position[1];
            this.stuck_last_position[2] = cur_z;
            this.stuck_check_time = 0;
          }

          const steered_yaw = this.compute_steered_yaw(goal[0], goal[2]);
          this.turn_toward_yaw(steered_yaw, delta_time);
          this.advance_with_root_motion();
          this.puppy.transform.update_matrix();
        }
      }
    }

    this.puppy.shadow?.update_shadow(
      this.puppy.transform.position[0],
      this.puppy.transform.position[1],
      this.puppy.transform.position[2]
    );
  }
}